import React, { useMemo } from 'react';
import { Box, MenuItem, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';

import CustomDatePicker from '../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import '../../styles/bankTokens.css';

export const PERIOD_MODE = { YEAR: 'year', RANGE: 'range' };

/** How far back the year list goes; older years stay reachable via range mode. */
const YEARS_BACK = 10;

/**
 * Rows are stored with UTC timestamps, so every bound is built in UTC to avoid
 * slipping a day for users in negative-offset timezones.
 */
export const yearBounds = (year) => ({
    start: new Date(Date.UTC(year, 0, 1)),
    end: new Date(Date.UTC(year, 11, 31, 23, 59, 59)),
});

export const startOfUtcDay = (date) =>
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));

export const endOfUtcDay = (date) =>
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59));

const toggleGroupSx = {
    height: 32,
    backgroundColor: 'var(--bank-surface-muted, #f7f8fc)',
    borderRadius: 'var(--bank-radius-sm, 8px)',
    '& .MuiToggleButton-root': {
        textTransform: 'none',
        fontSize: '0.72rem',
        fontWeight: 700,
        px: 1.25,
        py: 0.25,
        border: '1px solid var(--bank-border, #e4e7f2)',
        color: 'var(--bank-text-muted, #6b7186)',
        '&.Mui-selected': {
            color: '#fff',
            backgroundColor: 'var(--Foundation-Blue-Normal, #304FFF)',
            '&:hover': { backgroundColor: 'var(--Foundation-Blue-Normal, #304FFF)' },
        },
    },
};

const yearFieldSx = {
    width: 116,
    '& .MuiInputBase-root': {
        height: 40,
        borderRadius: 'var(--bank-radius-sm, 8px)',
        backgroundColor: 'var(--bg-input, #F5F5F6)',
        fontWeight: 700,
    },
    '& fieldset': { border: '1px solid var(--bank-border, #e4e7f2)' },
};

/**
 * The period a bank screen is scoped to, in one of two shapes: a whole calendar
 * year, or an explicit start/end range. Both screens share this so the same
 * account always reports the same figures for the same period.
 */
const BankPeriodPicker = ({
    mode = PERIOD_MODE.YEAR,
    onModeChange,
    year,
    onYearChange,
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    disabled = false,
}) => {
    const { t } = useTranslation();

    const years = useMemo(() => {
        const current = new Date().getFullYear();
        const list = [];
        for (let y = current + 1; y >= current - YEARS_BACK; y -= 1) list.push(y);
        if (year != null && !list.includes(year)) {
            list.push(year);
            list.sort((a, b) => b - a);
        }
        return list;
    }, [year]);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
                exclusive
                size="small"
                value={mode}
                onChange={(_, next) => next && onModeChange?.(next)}
                sx={toggleGroupSx}
                disabled={disabled}
            >
                <ToggleButton value={PERIOD_MODE.YEAR}>{t('Year')}</ToggleButton>
                <ToggleButton value={PERIOD_MODE.RANGE}>{t('DateRange')}</ToggleButton>
            </ToggleButtonGroup>

            {mode === PERIOD_MODE.YEAR ? (
                <TextField
                    select
                    size="small"
                    label={t('Year')}
                    value={year ?? ''}
                    onChange={(event) => onYearChange?.(Number(event.target.value))}
                    disabled={disabled}
                    sx={yearFieldSx}
                >
                    {years.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </TextField>
            ) : (
                <>
                    <Box sx={{ width: 165 }}>
                        <CustomDatePicker
                            date={startDate}
                            onDateChange={(next) => next && onStartDateChange?.(next)}
                            label={t('StartDate')}
                            height="40px"
                            disabled={disabled}
                        />
                    </Box>
                    <Box sx={{ width: 165 }}>
                        <CustomDatePicker
                            date={endDate}
                            onDateChange={(next) => next && onEndDateChange?.(next)}
                            label={t('EndDate')}
                            height="40px"
                            disabled={disabled}
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default BankPeriodPicker;
